import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { PDP_SECTION_LABELS, readPdpSections, type PdpSectionId } from "@/lib/catalog/pdp-sections";
import { COLLECTIONS, FAMILIES, GENDERS, CONCENTRATIONS, DEFAULT_PRODUCT_BENEFITS, type CatalogProduct } from "@/lib/catalog/products";
import { deleteAdminProductFn, getAdminProductFn, listAdminProductsFn, saveAdminProductFn } from "@/lib/admin/products/products.functions";
import { mapDatabaseProduct, type DatabaseProductRow } from "@/lib/catalog/product-mapper";
import { fallbackProductImage } from "@/lib/catalog/fallback-images";
import { logAction } from "@/lib/admin/audit/log";
import { useAdminMoney } from "@/lib/admin/settings";
import { PageHeader } from "@/components/admin/PageHeader";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { invalidateProductImages } from "@/lib/catalog/product-images";
import { BOTTLE_SIZE_IDS, sizePrices, type SizePrices } from "@/lib/catalog/size-pricing";
import { SALES_CHANNELS, normalizeSalesChannels } from "@/lib/catalog/sales-channels";
import { notify } from "@/lib/admin/notify";
import { ProductPrice } from "@/components/common/ProductPrice";
import { PricingSpreadsheet } from "@/components/admin/PricingSpreadsheet";

export const Route = createFileRoute("/admin/products")({
  head: () => ({ meta: [
    { title: "Products — CB16 Admin" },
    { name: "description", content: "Manage CB16 products and product detail content." },
    { name: "robots", content: "noindex, nofollow" },
    { property: "og:title", content: "Products — CB16 Admin" },
    { property: "og:description", content: "Manage CB16 products and product detail content." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: ProductsPage,
});

function ProductsPage() {
  const listProducts = useServerFn(listAdminProductsFn);
  const deleteProduct = useServerFn(deleteAdminProductFn);
  const saveProduct = useServerFn(saveAdminProductFn);
  const getProduct = useServerFn(getAdminProductFn);
  const [rows, setRows] = useState<CatalogProduct[]>([]);
  const [customImageIds, setCustomImageIds] = useState<Set<string>>(new Set());
  const money = useAdminMoney();
  const [q, setQ] = useState("");
  const [collection, setCollection] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [imageFilter, setImageFilter] = useState<"" | "custom" | "demo">("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<CatalogProduct | null>(null);
  const [creating, setCreating] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const refresh = async () => {
    const data = (await listProducts()) as DatabaseProductRow[];
    setCustomImageIds(new Set(data.filter((row) => !!row.cover_image).map((row) => row.id)));
    setRows(data.map((row) => mapDatabaseProduct(row)));
  };
  useEffect(() => { void refresh(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((p) => {
      if (collection && p.collectionId !== collection) return false;
      if (status && (p.status ?? "active") !== status) return false;
      if (imageFilter === "custom" && !customImageIds.has(p.id)) return false;
      if (imageFilter === "demo" && customImageIds.has(p.id)) return false;
      if (term && !(p.name.toLowerCase().includes(term) || p.slug.toLowerCase().includes(term) || (p.sku ?? "").toLowerCase().includes(term))) return false;
      return true;
    });
  }, [rows, q, collection, status, imageFilter, customImageIds]);

  const customCount = useMemo(() => rows.filter((p) => customImageIds.has(p.id)).length, [rows, customImageIds]);
  const demoCount = rows.length - customCount;

  const toggleAll = (on: boolean) => setSelected(on ? new Set(filtered.map((p) => p.id)) : new Set());
  const toggleOne = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulk = async (action: "publish" | "archive" | "delete") => {
    if (selected.size === 0) return;
    if (action === "delete" && !confirm(`Delete ${selected.size} product(s)?`)) return;
    for (const id of selected) {
      const product = rows.find((row) => row.id === id);
      if (!product) continue;
      if (action === "delete") await deleteProduct({ data: { id } });
      else await saveProduct({ data: toSaveInput({ ...product, status: action === "publish" ? "active" : "archived" }) });
      logAction({ entity: "product", entityId: id, action: action === "delete" ? "delete" : "status_change", summary: `Bulk ${action}` });
    }
    setSelected(new Set());
    await refresh();
  };

  const duplicate = async (p: CatalogProduct) => {
    const copy = { ...p, name: `${p.name} (copy)`, slug: `${p.slug}-copy-${Math.random().toString(36).slice(2, 6)}`, status: "coming-soon" as const };
    delete (copy as Partial<CatalogProduct>).id;
    const created = await saveProduct({ data: toSaveInput(copy) });
    logAction({ entity: "product", entityId: created.id, action: "create", summary: `Duplicated from ${p.name}` });
    await refresh();
  };

  const openEditor = async (id: string) => {
    setOpenError(null);
    try {
      const row = await getProduct({ data: { id } });
      console.info("[product-editor] loaded from database", {
        productId: row.id,
        coverImage: row.cover_image,
        updatedAt: row.updated_at,
      });
      const databaseProduct = mapDatabaseProduct(row as DatabaseProductRow);
      // The storefront mapper intentionally supplies demo artwork when the DB
      // value is null. The editor must never do that: its field represents only
      // the value actually committed to products.cover_image.
      setEditing({ ...databaseProduct, img: row.cover_image ?? "" });
    } catch (error) {
      setOpenError(error instanceof Error ? error.message : "Could not reload this product from the database.");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Products" subtitle={`${rows.length} products in catalog`} action={
        <button onClick={() => setCreating(true)} className="border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-black/85">+ New product</button>
      }/>
      {openError && <div className="border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{openError}</div>}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Products" value={rows.length} />
        <StatCard label="Custom Images" value={customCount} tone="emerald" />
        <StatCard label="Demo Images Remaining" value={demoCount} tone="amber" />
      </div>

      <PricingSpreadsheet onApplied={refresh} />

      <div className="flex flex-wrap items-center gap-2 border border-black/10 bg-white p-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, slug, SKU" className="flex-1 min-w-[200px] border border-black/15 px-3 py-2 text-sm outline-none focus:border-black/50"/>
        <select value={collection} onChange={(e) => setCollection(e.target.value)} className="border border-black/15 px-2 py-2 text-sm">
          <option value="">All collections</option>
          {COLLECTIONS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-black/15 px-2 py-2 text-sm">
          <option value="">All statuses</option>
          <option value="active">Published</option>
          <option value="coming-soon">Draft</option>
          <option value="archived">Archived</option>
        </select>
        <select value={imageFilter} onChange={(e) => setImageFilter(e.target.value as "" | "custom" | "demo")} className="border border-black/15 px-2 py-2 text-sm">
          <option value="">All products</option>
          <option value="custom">Custom images</option>
          <option value="demo">Demo images missing</option>
        </select>
        {selected.size > 0 && (
          <>
            <span className="ml-2 text-xs text-black/60">{selected.size} selected</span>
            <button onClick={() => bulk("publish")} className="border border-black/15 px-2 py-1 text-[11px] uppercase tracking-widest">Publish</button>
            <button onClick={() => bulk("archive")} className="border border-black/15 px-2 py-1 text-[11px] uppercase tracking-widest">Archive</button>
            <button onClick={() => bulk("delete")} className="border border-red-300 px-2 py-1 text-[11px] uppercase tracking-widest text-red-700">Delete</button>
          </>
        )}
      </div>

      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">
              <th className="px-3 py-2 w-8"><input type="checkbox" checked={selected.size > 0 && selected.size === filtered.length} onChange={(e) => toggleAll(e.target.checked)}/></th>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Image</th>
              <th className="px-3 py-2">Collection</th>
              <th className="px-3 py-2">Starting from</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-black/5">
                <td className="px-3 py-2"><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleOne(p.id)}/></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-3">
                    {p.img && <img src={p.img} alt="" className="h-10 w-10 object-cover border border-black/10"/>}
                    <div>
                      <div className="font-medium text-black">{p.name}</div>
                      <div className="text-[11px] text-black/50">/{p.slug}{p.sku ? ` · ${p.sku}` : ""}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2"><ImageStatusBadge custom={customImageIds.has(p.id)} /></td>
                <td className="px-3 py-2 text-black/70">{COLLECTIONS.find((c) => c.id === p.collectionId)?.name ?? "—"}</td>
                <td className="px-3 py-2 text-black/80">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-black/40 uppercase tracking-wider">Starting From</span>
                    <ProductPrice 
                      price={lowestValidPrice(p)} 
                      discountPercent={p.discountPercent} 
                      size="sm" 
                      showDiscountLabel={false}
                    />
                  </div>
                </td>
                <td className="px-3 py-2"><StatusBadge status={p.status ?? "active"}/></td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => void openEditor(p.id)} className="border border-black/15 px-2 py-1 text-[11px] hover:border-black/50">Edit</button>
                    <button onClick={() => duplicate(p)} className="border border-black/15 px-2 py-1 text-[11px] hover:border-black/50">Duplicate</button>
                    <button onClick={() => { if (confirm(`Delete ${p.name}?`)) void deleteProduct({ data: { id: p.id } }).then(refresh); }} className="border border-red-200 px-2 py-1 text-[11px] text-red-700 hover:border-red-500">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-black/50">No products match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(editing || creating) && (
        <ProductEditor product={editing} onSaved={refresh} onClose={() => { setEditing(null); setCreating(false); }} />
      )}
    </div>
  );
}

function ProductEditor({ product, onClose, onSaved }: { product: CatalogProduct | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const saveProduct = useServerFn(saveAdminProductFn);
  const getProduct = useServerFn(getAdminProductFn);
  const router = useRouter();
  const isNew = !product;
  const [form, setForm] = useState<Partial<CatalogProduct>>(product ?? {
    name: "", slug: "", family: FAMILIES[0], gender: "Unisex", concentration: "EDP",
    notes: "", keyNotes: [], price: 0, prices: { "10": 0, "20": 0, "50": 0, "100": 0 }, img: "", collectionId: "signatures",
    createdAt: new Date().toISOString(), status: "coming-soon",
  });
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const set = <K extends keyof CatalogProduct>(k: K, v: CatalogProduct[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };
  const [pickerOpen, setPickerOpen] = useState(false);
  const [gallerySlot, setGallerySlot] = useState<number | null>(null);
  const setGalleryImage = (slot: number, url: string) => {
    setForm((f) => {
      const next = [0, 1, 2, 3].map((i) => galleryAt(f.gallery, i));
      next[slot] = url;
      while (next.length > 0 && !next[next.length - 1]) next.pop();
      return { ...f, gallery: next };
    });
    setDirty(true);
    setGallerySlot(null);
  };

  // Keep the notes field as free-form text so commas, spaces and punctuation
  // survive while typing; parsing into keyNotes happens alongside it.
  const [notesText, setNotesText] = useState<string>(
    product?.notes?.trim() ? product.notes : (product?.keyNotes ?? []).join(", "),
  );
  const save = async () => {
    if (saving) return; // ignore duplicate clicks while a save is in flight
    setSaveError(null);
    if (!form.name || !form.slug) { setSaveError("Name and slug are required."); return; }
    const priceValues = sizePrices(form);
    const invalidSize = (["50", "100"] as const).find(
      (k) => !Number.isFinite(priceValues[k]) || priceValues[k] <= 0,
    );
    if (invalidSize) {
      setSaveError(`Enter a valid ${invalidSize} ML price greater than zero.`);
      return;
    }
    if ((form.img ?? "").startsWith("data:")) {
      setSaveError("Base64 images are not supported. Choose the image from the Media Library.");
      return;
    }
    setSaving(true);
    try {
      const input = toSaveInput({ ...product, ...form } as CatalogProduct);
      const before = input.id ? await getProduct({ data: { id: input.id } }) : null;
      console.info("[product-editor] before save", {
        productId: input.id ?? null,
        currentDatabaseCoverImage: before?.cover_image ?? null,
        submittedCoverImage: input.coverImage,
      });
      const saved = (await saveProduct({ data: input })) as { id: string; name: string; cover_image: string | null };
      // Verify the image actually landed in the database — a silent mismatch is
      // what made the storefront keep showing the seeded demo artwork.
      if ((saved.cover_image ?? null) !== (input.coverImage ?? null)) {
        setSaveError("The cover image was not stored in the database. Please pick it again from the Media Library and save.");
        notify.error("Unable to save product.", "Please try again.");
        return;
      }
      const committed = await getProduct({ data: { id: saved.id } });
      console.info("[product-editor] database after save", {
        productId: committed.id,
        coverImage: committed.cover_image,
        updatedAt: committed.updated_at,
      });
      if ((committed.cover_image ?? null) !== (input.coverImage ?? null)) {
        setSaveError("The saved product could not be verified in the database. The editor will remain open.");
        notify.error("Unable to save product.", "Please try again.");
        return;
      }
      logAction({ entity: "product", entityId: saved.id, action: isNew ? "create" : "update", summary: `${isNew ? "Created" : "Updated"} ${saved.name}` });
      invalidateProductImages();
      await router.invalidate();
      await onSaved();
      setDirty(false);
      notify.success("Product saved successfully.");
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 1800);
    } catch (err) {
      console.error("[products] save failed:", err);
      setSaveError(err instanceof Error ? err.message : "Could not save this product.");
      notify.error("Unable to save product.", "Please try again.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between border-b border-black/10 pb-3">
          <h3 className="text-lg font-semibold">{isNew ? "New product" : `Edit · ${product?.name}`}</h3>
          <button onClick={onClose} className="text-sm text-black/60 hover:text-black">✕</button>
        </div>
        {saveError && (
          <div className="mb-4 border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</div>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Name"><input className="input" value={form.name ?? ""} onChange={(e) => set("name", e.target.value)}/></Field>
          <Field label="Slug"><input className="input" value={form.slug ?? ""} onChange={(e) => set("slug", e.target.value)}/></Field>
          <Field label="SKU"><input className="input" value={form.sku ?? ""} onChange={(e) => set("sku", e.target.value)}/></Field>
          <PriceFields
            prices={sizePrices(form)}
            onChange={(next) => { set("prices", next); set("price", next["50"]); }}
          />
          <Field label="Discount (%)">
            <input 
              type="number" 
              className="input" 
              min="0" 
              max="100" 
              value={form.discountPercent ?? 0} 
              onChange={(e) => set("discountPercent", parseInt(e.target.value) || 0)}
              placeholder="0-100"
            />
          </Field>
          <Field label="Collection">
            <select className="input" value={form.collectionId} onChange={(e) => set("collectionId", e.target.value as CatalogProduct["collectionId"])}>
              {COLLECTIONS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Family">
            <select className="input" value={form.family} onChange={(e) => set("family", e.target.value as CatalogProduct["family"])}>
              {FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Gender">
            <select className="input" value={form.gender} onChange={(e) => set("gender", e.target.value as CatalogProduct["gender"])}>
              {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Concentration">
            <select className="input" value={form.concentration} onChange={(e) => set("concentration", e.target.value as CatalogProduct["concentration"])}>
              {CONCENTRATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className="input" value={form.status ?? "coming-soon"} onChange={(e) => set("status", e.target.value as CatalogProduct["status"])}>
              <option value="active">Published</option>
              <option value="coming-soon">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
          <Field label="Cover image URL" full>
            <span className="flex items-center gap-2">
              <input className="input w-full" value={form.img ?? ""} onChange={(e) => set("img", e.target.value)} placeholder="Media Library URL or /img/..."/>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="shrink-0 border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]"
              >
                Library
              </button>
            </span>
            <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(a) => set("img", a.url)} />
            {form.img && (
              <img src={form.img} alt="" className="mt-2 h-20 w-20 border border-black/10 object-cover" />
            )}
          </Field>
          <Field label="Product gallery images" full>
            <p className="mb-2 text-[11px] text-black/50">
              Image 1 is the cover image above. Slots 2–5 are shown as thumbnails on the product page.
            </p>
            <div className="flex flex-wrap gap-3">
              {[1, 2, 3, 4].map((idx) => {
                const url = galleryAt(form.gallery, idx - 1);
                return (
                  <div key={idx} className="flex w-36 flex-col gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-black/50">
                      Gallery image {idx + 1}
                    </span>
                    <div className="flex h-24 w-full items-center justify-center border border-black/10 bg-black/[0.02]">
                      {url ? (
                        <img src={url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-black/30">Empty</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setGallerySlot(idx - 1)}
                      className="border border-black/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]"
                    >
                      {url ? "Replace" : "Select"}
                    </button>
                    {url && (
                      <button
                        type="button"
                        onClick={() => setGalleryImage(idx - 1, "")}
                        className="border border-black/15 px-2 py-1 text-[10px] uppercase tracking-widest text-black/60 hover:border-red-400 hover:text-red-600"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <MediaPicker
              open={gallerySlot !== null}
              onClose={() => setGallerySlot(null)}
              onSelect={(a) => { if (gallerySlot !== null) setGalleryImage(gallerySlot, a.url); }}
            />
          </Field>

          <Field label="Fragrance Notes (comma-separated)" full>
            <span className="text-xs text-muted-foreground">Edit the Fragrance Notes content here. “Show Fragrance Notes” below controls visibility only.</span>
            <input
              className="input"
              value={notesText}
              onChange={(e) => {
                const raw = e.target.value;
                setNotesText(raw);
                set("notes", raw);
                set("keyNotes", raw.split(",").map((s) => s.trim()).filter(Boolean));
              }}
            />
          </Field>
          <Field label="Short description" full><textarea className="input min-h-16" value={form.shortDescription ?? ""} onChange={(e) => set("shortDescription", e.target.value)}/></Field>
          <Field label="Product benefits (below Add to Cart)" full>
            <p className="mb-2 text-[11px] text-black/50">
              These benefits appear below Add to Cart on the product detail page. Untick a row or clear its text to hide it.
            </p>
            <div className="flex flex-col gap-2">
              {(form.benefits ?? DEFAULT_PRODUCT_BENEFITS).map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={benefit.enabled}
                    onChange={(e) => {
                      const next = [...(form.benefits ?? DEFAULT_PRODUCT_BENEFITS)];
                      next[i] = { ...next[i], enabled: e.target.checked };
                      set("benefits", next);
                    }}
                    title="Show on product page"
                  />
                  <input
                    className="input w-full"
                    value={benefit.text}
                    placeholder={`Benefit ${i + 1}`}
                    onChange={(e) => {
                      const next = [...(form.benefits ?? DEFAULT_PRODUCT_BENEFITS)];
                      next[i] = { ...next[i], text: e.target.value };
                      set("benefits", next);
                    }}
                  />
                </div>
              ))}
            </div>
          </Field>
          <Field label="Product page sections (below benefits)" full>
            <p className="mb-2 text-[11px] text-black/50">
              These sections appear as expandable rows on the product detail page. Untick to hide a section. For Delivery &amp; Shipping, put each point on its own line. Fragrance Notes text comes from the Fragrance Notes field above.
            </p>
            <div className="flex flex-col gap-3">
              {(Object.keys(PDP_SECTION_LABELS) as PdpSectionId[]).map((id) => {
                const all = readPdpSections(form.attributes, form.family);
                const updateSection = (patch: Partial<{ enabled: boolean; text: string }>) => {
                  const current = readPdpSections(form.attributes, form.family);
                  set("attributes", { ...(form.attributes ?? {}), pdpSections: { ...current, [id]: { ...current[id], ...patch } } });
                };
                return (
                  <div key={id} className="flex flex-col gap-1">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={all[id].enabled} onChange={(e) => updateSection({ enabled: e.target.checked })} />
                      Show “{PDP_SECTION_LABELS[id]}”
                    </label>
                    {id !== "notes" && (
                      <textarea className="input min-h-16" value={all[id].text} onChange={(e) => updateSection({ text: e.target.value })} />
                    )}
                  </div>
                );
              })}
            </div>
          </Field>
          <Field label="Sales channels" full>
            <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
              {SALES_CHANNELS.map((channel) => {
                const current = normalizeSalesChannels(form.salesChannels);
                const checked = current.includes(channel.id);
                return (
                  <label key={channel.id} className="flex items-center gap-2 text-sm" title={channel.description}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? Array.from(new Set([...current, channel.id]))
                          : current.filter((c) => c !== channel.id);
                        set("salesChannels", next.length > 0 ? next : ["individual"]);
                      }}
                    />
                    {channel.label}
                  </label>
                );
              })}
            </div>
          </Field>
          <Field label="SEO title" full><input className="input" value={form.metaTitle ?? ""} onChange={(e) => set("metaTitle", e.target.value)}/></Field>
          <Field label="Homepage visibility" full>
            <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.showInBestSellers === true}
                  onChange={(e) => set("showInBestSellers", e.target.checked)}
                />
                Show in Best Sellers
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.showInNewArrivals === true}
                  onChange={(e) => set("showInNewArrivals", e.target.checked)}
                />
                Show in New Arrivals
              </label>
            </div>
          </Field>
          <Field label="SEO description" full><textarea className="input min-h-16" value={form.metaDescription ?? ""} onChange={(e) => set("metaDescription", e.target.value)}/></Field>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="border border-black/15 px-4 py-2 text-[11px] uppercase tracking-widest">Cancel</button>
          <button
            onClick={save}
            disabled={saving || (!isNew && !dirty)}
            className="inline-flex items-center gap-2 border border-black bg-black px-4 py-2 text-[11px] uppercase tracking-widest text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving && (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden />
            )}
            {saving
              ? "Saving..."
              : justSaved
                ? "✓ Saved"
                : !isNew && !dirty
                  ? "No Changes"
                  : dirty && !isNew
                    ? "Save changes"
                    : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function galleryAt(gallery: string[] | undefined, index: number): string {
  return (gallery ?? [])[index] ?? "";
}

function toSaveInput(product: Partial<CatalogProduct>) {
  const status = product.status ?? "active";
  // `img` may currently hold a bundled demo fallback (used when the product has
  // no uploaded cover image). Never persist that placeholder to the database.
  const raw = (product.img ?? "").split("?v=")[0];
  const demo = fallbackProductImage(product.slug ?? "", product.categoryId ?? "personal-perfumes");
  const coverImage = !raw || raw === demo.split("?v=")[0] ? null : raw;
  return {
    id: product.id, slug: product.slug ?? "", name: product.name ?? "",
    family: product.family ?? "Floral", gender: product.gender ?? "Unisex",
    concentration: product.concentration ?? "EDP", notes: product.notes ?? "",
    keyNotes: product.keyNotes ?? [], prices: sizePrices(product),
    coverImage, badge: product.badge,
    categoryId: product.categoryId ?? "personal-perfumes",
    attributes: { ...(product.attributes ?? {}), collectionId: product.collectionId ?? "signatures",
      sku: product.sku ?? "", shortDescription: product.shortDescription ?? "",
      longDescription: product.longDescription ?? "", gallery: product.gallery ?? [], lifecycleStatus: status,
      // Homepage rail visibility (optional flags; default false).
      showInBestSellers: product.showInBestSellers === true,
      showInNewArrivals: product.showInNewArrivals === true,
      // Editable PDP benefit statements (text + per-row visibility).
      benefits: product.benefits ?? DEFAULT_PRODUCT_BENEFITS },
    isActive: status === "active",
    salesChannels: normalizeSalesChannels(product.salesChannels),
    discountPercent: product.discountPercent ?? 0,
  };
}

function lowestValidPrice(product: CatalogProduct): number {
  const values = Object.values(sizePrices(product)).filter((v) => v > 0);
  return values.length > 0 ? Math.min(...values) : 0;
}


function StatusBadge({ status }: { status: string }) {
  return <span className={`inline-block border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${statusClass(status)}`}>{statusLabel(status)}</span>;
}

function statusClass(status: string) {
  return status === "active" ? "border-emerald-300 bg-emerald-50 text-emerald-800"
    : status === "archived" ? "border-black/20 bg-black/5 text-black/60"
    : "border-amber-300 bg-amber-50 text-amber-800";
}

function statusLabel(status: string) {
  return status === "active" ? "Published" : status === "coming-soon" ? "Draft" : status;
}

function StatCard({ label, value, tone }: { label: string; value: number; tone?: "emerald" | "amber" }) {
  const cls = tone === "emerald" ? "text-emerald-800" : tone === "amber" ? "text-amber-800" : "text-black";
  return (
    <div className="border border-black/10 bg-white px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

function ImageStatusBadge({ custom }: { custom: boolean }) {
  const cls = custom ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800";
  return (
    <span className={`inline-block whitespace-nowrap border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${cls}`}>
      {custom ? "🟢 Custom image" : "🟡 Demo image"}
    </span>
  );
}

function PriceFields({ prices, onChange }: { prices: SizePrices; onChange: (next: SizePrices) => void }) {
  return (
    <>
      {BOTTLE_SIZE_IDS.map((size) => (
        <Field key={size} label={`${size} ML Price (₹)`}>
          <input
            type="number"
            min="0"
            step="1"
            className="input"
            value={prices[size]}
            onChange={(e) => onChange({ ...prices, [size]: Number(e.target.value) })}
          />
        </Field>
      ))}
    </>
  );
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/60">{label}</span>
      <style>{`.input{border:1px solid rgba(0,0,0,0.15);padding:0.5rem 0.75rem;font-size:14px;outline:none;background:white;} .input:focus{border-color:rgba(0,0,0,0.5);}`}</style>
      {children}
    </label>
  );
}
